import type { CoreConfig } from 'p2p-media-loader-core';
import { API_BASE } from '@/services/apiClient';

/**
 * Public WebTorrent trackers used as a defensive fallback. They are
 * notoriously unreliable: `tracker.openwebtorrent.com` is intermittently
 * down for weeks, all three are commonly blocked by adblock lists (uBlock
 * Origin's default filters), and the WSS handshake can stall behind
 * corporate firewalls. Without our self-hosted tracker as the first entry,
 * an outage at any of these silently kills lobby P2P. With it, the
 * fallbacks keep working for clients whose network reaches them.
 */
const PUBLIC_TRACKERS: readonly string[] = [
  'wss://tracker.openwebtorrent.com',
  'wss://tracker.webtorrent.dev',
  'wss://tracker.files.fm:7073/announce',
];

/**
 * Self-hosted bittorrent-tracker route on the API. Lives on the same Fly
 * machine as the rest of the backend (no additional infra, no extra DNS).
 * Always tried first because: (a) we control its uptime, (b) it's not on
 * adblock lists, (c) it shares the API origin so corporate firewalls that
 * already allow `api.cobstreams.com` don't need a new exception.
 *
 * The tracker is purely a peer-introduction service — it never sees video
 * bytes and never sees identity, so reusing the API host is safe.
 */
function selfHostedTrackerUrl(apiBase: string | null): string | null {
  if (!apiBase) return null;
  // apiBase is e.g. "https://api.cobstreams.com"; swap scheme to wss:// (or
  // ws:// for plain-HTTP dev backends).
  try {
    const u = new URL(apiBase);
    u.protocol = u.protocol === 'http:' ? 'ws:' : 'wss:';
    u.pathname = '/tracker';
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Final tracker list: self-hosted first (when API_BASE is set), then the
 * public fallbacks. Exported so tests can verify ordering.
 */
export function trackerListFor(apiBase: string | null = API_BASE): readonly string[] {
  const selfHosted = selfHostedTrackerUrl(apiBase);
  return selfHosted ? [selfHosted, ...PUBLIC_TRACKERS] : [...PUBLIC_TRACKERS];
}

/**
 * Google's free STUN servers. STUN-only — no TURN. Per the design discussion:
 * STUN gets ~80-90% of users connected; the rest fall back to origin (no
 * degradation, just no P2P benefit for them).
 */
const STUN_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:global.stun.twilio.com:3478' },
];

/**
 * Swarm ID prefix. Different majors here would create entirely separate
 * swarms across deploys — useful if we ever ship a breaking change to the
 * segment-ID hashing scheme.
 */
const SWARM_VERSION = 'cs/v1';

export type P2pMode =
  /** In a lobby together. swarmId = lobby + ref so members in different rooms
   *  watching the same movie don't accidentally pool — keeps each lobby's
   *  swarm small + cooperative. */
  | { kind: 'lobby'; lobbyId: string; refKey: string }
  /** Solo or non-lobby live sports. swarmId = eventId + provider so all
   *  concurrent viewers of the same event find each other. */
  | { kind: 'sports'; eventId: string; provider: string };

/**
 * Build the swarmId string used by P2PML to scope peer matching. Knowledge of
 * a lobby's swarmId is no more sensitive than knowledge of the 8-char lobbyCode
 * (which is already the join capability): joining the swarm only lets peers
 * exchange the SAME segments they could already fetch from origin.
 */
export function buildSwarmId(mode: P2pMode): string {
  if (mode.kind === 'lobby') {
    return `${SWARM_VERSION}/lobby/${mode.lobbyId}/${mode.refKey}`;
  }
  return `${SWARM_VERSION}/sports/${mode.eventId}/${mode.provider}`;
}

/**
 * Build the full P2PML core config for a given mode. Hooked into
 * `HlsJsP2PEngine.injectMixin(Hls)` via the `p2p.core` field.
 */
export function buildP2pCoreConfig(mode: P2pMode): Partial<CoreConfig> {
  return {
    swarmId: buildSwarmId(mode),
    announceTrackers: [...trackerListFor()],
    rtcConfig: { iceServers: STUN_ICE_SERVERS },
    // Modest concurrency caps so a tab on a flaky connection doesn't open 30
    // peer connections and bog the device down. Defaults are reasonable but
    // we cap explicitly for predictability.
    simultaneousHttpDownloads: 3,
    simultaneousP2PDownloads: 3,
    // Disable P2P uploads on cellular — TODO once we wire connectivity
    // detection. For now, always-on per the user's "ship it" preference.
    isP2PDisabled: false,
    isP2PUploadDisabled: false,
  };
}
